import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import { createRomaneio } from '../../services/Romaneio/romaneioService';
import useUserRoles from '../../hooks/Home/useUserRoles';
import ErrorModal from '../Modals/ErrorModal';
import SuccessModal from '../Modals/SuccessModal';
import DateInput from '../Utils/DateInput';
import HourInput from '../Utils/HourInput';
import HarvestInput from '../Utils/HarvestInput';
import PersonInput from '../Utils/PersonInput';
import LotInput from '../Utils/LotInput';
import FarmInput from '../Utils/FarmInput';
import SiloInput from '../Utils/SiloInput';
import ProductInput from '../Utils/ProductInput';
import BalanceInput from '../Utils/BalanceInput';
import InputField from '../Utils/InputField';
import TextAreaInput from '../Utils/TextAreaInput';
import WeightInput from '../Utils/WeightInput';
import DiscountInput from '../Utils/DiscountInput';
import DecimalInput from '../Utils/DecimalInput';
import useDiscountCalculation from "../../hooks/Romaneio/useDiscountCalculation";
import useFormatRomaneioPayload from '../../hooks/Romaneio/useFormatRomaneioPayload';
import { getCurrentDate, formatTimeToHHMM } from '../Utils/utils';
import DriverInput from '../Utils/DriverInput';

const CreateRomaneioModal = ({ show, handleClose, refreshRomaneios }) => {
  const { register, handleSubmit, formState: { errors }, reset, setValue, control, watch, trigger } = useForm({
    defaultValues: {
      entry: false,
      exit: false,
      grossWeight: false,
      netWeight: true,
      entryDate: getCurrentDate(),
      entryHour: formatTimeToHHMM(new Date())
    }
  });
  const userRoles = useUserRoles(); 
  const hasAdminRole = userRoles.includes('ADMIN');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const selectedProductId = watch("productId");
  const { formatPayload } = useFormatRomaneioPayload();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // eslint-disable-next-line
  const [isReadOnlyInputs, setIsReadOnlyInputs] = useState([]);
  
  useDiscountCalculation(control, setValue, watch("discountId"));

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const formattedData = formatPayload(data);
      
      await createRomaneio(formattedData);
      setSuccessMessage("Romaneio criado com sucesso!");
      setShowSuccessModal(true);
      reset();
    } catch (error) {
      setErrorMessage(error.message || "Erro ao criar romaneio.");
      setShowErrorModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseErrorModal = () => setShowErrorModal(false);
  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    refreshRomaneios();
    handleClose();
  };

  useEffect(() => {
    if (!show) {
      reset();
    }
  }, [show, reset]);

  useEffect(() => {
    if (selectedProductId?.discount) {
      setValue('discountId', { id: selectedProductId.discount.id, name: selectedProductId.discount.description });
    } else {
      setValue('discountId', null);
    }

    trigger("discountId");
  }, [selectedProductId, setValue, trigger]);

  return (
    <>
      <Modal show={show} onHide={handleClose} centered backdrop="static" keyboard={false} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Novo Romaneio</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Row>
              <Col md={2}>
                <Controller
                  name="entryDate"
                  control={control}
                  rules={{ required: 'Data de entrada é obrigatória' }}
                  render={({ field, fieldState }) => (
                    <DateInput
                      isEditing = { false }
                      label="Data"
                      value={field.value || getCurrentDate()}
                      onChange={field.onChange}
                      error={fieldState.error}
                      placeholder="__/__/____"
                    />
                  )}
                />
              </Col>
              <Col md={1}>
                <HourInput
                  label="Hora"
                  value={formatTimeToHHMM(new Date())}
                  name="entryHour"
                  placeholder="__:__"
                  control={{ register, setValue, watch }}
                  errors={errors}
                  rules={{ required: 'Hora de entrada é obrigatória' }}
                />
              </Col>
              <Col md={2}>
                <div className='mb-3'>
                  <p className="mb-0">Tipo</p>
                  {['entry', 'exit'].map((field) => (
                    <Form.Group controlId={field} key={field} style={field === 'entry' ? { marginBottom: '-3px' } : {}}>
                    <Form.Check 
                      className='mb-0'
                      type="checkbox"
                      label={field === 'entry' ? 'Entrada' : 'Saída'}
                      {...register(field, {
                      onChange: (e) => {
                        setValue(field === 'entry' ? 'exit' : 'entry', false);
                      },
                      validate: () => {
                        const otherField = field === 'entry' ? 'exit' : 'entry';
                        return watch(field) || watch(otherField) || 'Selecione o tipo do romaneio';
                      },
                      })}
                    />
                    </Form.Group>
                  ))}
                    {errors.entry && ( <p className="text-danger mb-0">{errors.entry.message}</p> )}
                </div>
              </Col>
              <Col md={5} style={{ display: 'none' }}>
                <Controller
                  name="harvestId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <HarvestInput
                      value={field.value}
                      createdNew={true}
                      onChange={(selected) => {
                        setValue('harvestId', selected);
                      }}
                      onCreated={(created) => {
                        setValue('harvestId', { id: created.id, name: created.name });
                      }}
                      label="Safra"
                    />
                  )}
                />
              </Col>
              <Col md={2}>
                <Controller
                  name="licensePlate"
                  control={control}
                  rules={{ required: 'A placa é obrigatória.' }}
                  render={({ field, fieldState }) => (
                    <DriverInput
                      value={field.value}
                      label="Placa"
                      isInvalid={!!fieldState.error}
                      onChange={(selected) => {
                        const plate = selected?.licensePlate || '';
                        const driver = selected?.id ? { id: selected.id, name: selected.name } : null;

                        setValue('licensePlate', plate);
                        setValue('driverId', driver);
                        trigger(['licensePlate', 'driverId']);
                      }}
                    />
                  )}
                />
              </Col>
              <Col md={5}>
                <Controller
                    name="driverId"
                    control={control}
                    rules={{ required: 'Motorista é obrigatório' }}
                    render={({ field, fieldState }) => (
                      <PersonInput
                        value={field.value}
                        createdNew={true}
                        onChange={(selected) => {
                          setValue('driverId', selected);
                          trigger('driverId');
                        }}
                        onCreated={(created) => {
                          setValue('driverId', { id: created.id, name: created.name });
                        }}
                        label="Motorista"
                        isInvalid={!!fieldState.error}
                        type={['isCustomer', 'isProducer', 'isSupplier' , 'isDriver']}
                      />
                    )}
                  />
                </Col>
            </Row>
            <Row>
              <Col md={4}  style={{ display: 'none' }}>
                <Controller
                  name="lotId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <LotInput
                      value={field.value}
                      createdNew={true}
                      onChange={(selectedLot) => {
                        setValue('lotId', { id: selectedLot?.id, description: selectedLot?.description || '' });
                        if (selectedLot) {
                          
                          if(selectedLot.lot?.farm) {
                            setValue('farmId', { id: selectedLot.lot.farm?.id || null, name: selectedLot.lot.farm?.name || '' });
                          } else {
                            setValue('farmId', null);
                          }

                          if(selectedLot.lot?.person) {
                            setValue('originPersonId', { id: selectedLot.lot.person?.id || null, name: selectedLot.lot.person?.name || '' });
                          } else {
                            setValue('originPersonId', null);
                          }

                          if(selectedLot.lot?.silo) {
                            setValue('siloId', { id: selectedLot.lot.silo?.id || null, name: selectedLot.lot.silo?.name || '' });
                          } else {
                            setValue('siloId', null);
                          }

                          if(selectedLot.lot?.product) {
                            setValue('productId', { id: selectedLot.lot.product?.id || null, description: selectedLot.lot.product?.description || '' });
                          } else {
                            setValue('productId', null);
                          }
                        }
                      }}
                      onCreated={(created) => {
                        setValue('lotId', { id: created.id, name: created.name });
                      }}
                      label="Lote"
                    />
                  )}
                />
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Controller
                  name="originPersonId"
                  control={control}
                  rules={{ required: 'Produtor / Cliente é obrigatório' }}
                  render={({ field, fieldState }) => (
                    <PersonInput
                      value={field.value}
                      createdNew={true}
                      onChange={(selected) => {
                        setValue('originPersonId', selected);
                        setValue('ratePerc', selected?.discount);
                        trigger('originPersonId');
                      }}
                      onCreated={(created) => {
                        setValue('originPersonId', { id: created.id, name: created.name });
                        setValue('ratePerc', created?.discount);
                      }}
                      label="Produtor / Cliente"
                      isInvalid={!!fieldState.error}
                      type={['isCustomer', 'isProducer', 'isSupplier' , 'isDriver']}
                    />
                  )}
                />
              </Col>
              <Col md={6} style={{ display: 'none' }}>
                <Controller
                  name="destinyPersonId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <PersonInput
                      value={field.value}
                      createdNew={true}
                      onChange={(selected) => {
                        setValue('destinyPersonId', selected);
                        trigger('destinyPersonId');
                      }}
                      onCreated={(created) => {
                        setValue('destinyPersonId', { id: created.id, name: created.name });
                      }}
                      label="Produtor / Cliente / Destino"
                      isInvalid={!!fieldState.error}
                      type={['isCustomer', 'isProducer', 'isSupplier' , 'isDriver']}
                    />
                  )}
                />
              </Col>
              <Col md={4}>
                <Controller
                  name="productId"
                  control={control}
                  rules={{ required: 'Cultura é obrigatório' }}
                  render={({ field, fieldState }) => (
                    <ProductInput
                      value={field.value}
                      createdNew={true}
                      onChange={(selected) => {
                        setValue('productId', selected);
                        trigger('productId');
                      }}
                      onCreated={(created) => {
                        setValue('productId', { id: created.id, name: created.name });
                      }}
                      label="Cultura"
                      isInvalid={!!fieldState.error}
                    />
                  )}
                />
              </Col>
              <Col md={2}>
                <InputField
                  label="Número NF"
                  name="numberNF"
                  placeholder="Número NF"
                  register={register}
                />
              </Col>
              <Col md={2}>
                <Controller
                  name="weightNF"
                  control={control}
                  render={({ field }) => (
                    <WeightInput
                      value={field.value}
                      onChange={field.onChange}
                      label="Peso NF"
                    />
                  )}
                />
              </Col>
            </Row>
            <Row>
              <Col md={6} style={{ display: 'none' }}>
                <Controller
                  name="farmId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <FarmInput
                      value={field.value}
                      createdNew={true}
                      onChange={(selected) => {
                        setValue('farmId', selected);
                      }}
                      onCreated={(created) => {
                        setValue('farmId', { id: created.id, name: created.name });
                      }}
                      label="Fazenda"
                    />
                  )}
                />
              </Col>
              <Col md={6} style={{ display: 'none' }}>
                <Controller
                  name="siloId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <SiloInput
                      value={field.value}
                      createdNew={true}
                      onChange={(selected) => {
                        setValue('siloId', selected);
                      }}
                      onCreated={(created) => {
                        setValue('siloId', { id: created.id, name: created.name });
                      }}
                      label="Silo"
                    />
                  )}
                />
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Controller
                  name="discountId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <DiscountInput
                      value={watch("discountId")}
                      createdNew={hasAdminRole}
                      onChange={(selected) => {
                        setValue('discountId', selected);
                      }}
                      onCreated={(created) => {
                        setValue('discountId', { id: created.id, name: created.name });
                      }}
                      label="Tabela de Descontos"
                      isInvalid={!!fieldState.error}
                      readOnly={!watch("productId")}
                      product={watch("productId")}
                    />
                  )}
                />
              </Col>
              <Col md={2}>
                <Controller
                  name="balance"
                  control={control}
                  render={({ field }) => (
                    <BalanceInput
                      value={field.value}
                      onChange={field.onChange}
                      label="Peso Bruto"
                      userRoles={!hasAdminRole}
                    />
                  )}
                />
              </Col>
              <Col md={2}>
                <Controller
                  name="tare"
                  control={control}
                  render={({ field }) => (
                    <BalanceInput
                      value={field.value}
                      onChange={field.onChange}
                      label="Tara"
                      userRoles={!hasAdminRole}
                    />
                  )}
                />
              </Col>
              <Col md={2}>
                <Controller
                  name="labelWeightNotDiscount"
                  control={control}
                  render={({ field, fieldState }) => (
                    <WeightInput
                      value={field.value}
                      onChange={field.onChange}
                      label="Peso Líq. s/Desc"
                      isInvalid={!!fieldState.error}
                      readOnly={true}
                      isBold={true}
                    />
                  )}
                />
              </Col>
              <Col md={2}>
                <Controller
                  name="labelWeightAndRate"
                  control={control}
                  render={({ field, fieldState }) => (
                    <WeightInput
                      value={field.value}
                      onChange={field.onChange}
                      label="Peso Líq. c/Desc + Tx"
                      isInvalid={!!fieldState.error}
                      readOnly={true}
                      isBold={true}
                    />
                  )}
                />
              </Col>
            </Row>
            <Row>
              <Col md={3} style={{ display: 'none' }}>
                <Controller
                  name="exitDate"
                  control={control}
                  render={({ field, fieldState }) => (
                    <DateInput
                      isEditing = { false }
                      label="Data"
                      value={field.value}
                      onChange={field.onChange}
                      error={fieldState.error}
                      placeholder="__/__/____"
                    />
                  )}
                />
              </Col>
              <Col md={2} style={{ display: 'none' }}>
                <HourInput
                  label="Hora"
                  name="exitHour"
                  placeholder="__:__"
                  control={{ register, setValue, watch }}
                  errors={errors}
                />
              </Col>
            </Row>
            <Row>
              <Col xs={6} md={2}>
                <Controller
                  name="umidade"
                  control={control}
                  render={({ field, fieldState }) => (
                    <DecimalInput
                      value={field.value}
                      onChange={field.onChange}
                      label="Umidade %"
                      placeholder=" "
                      isInvalid={!!fieldState.error}
                    />
                  )}
                />
              </Col>
              <Col xs={6} md={2}>
                <Controller
                  name="umidadeWeight"
                  control={control}
                  render={({ field, fieldState }) => (
                    <WeightInput
                      value={field.value}
                      onChange={field.onChange}
                      label="Peso Umidade"
                      placeholder=""
                      isInvalid={!!fieldState.error}
                      readOnly={true}
                    />
                  )}
                />
              </Col>
              <Col xs={6} md={2}>
                <Controller
                  name="impureza"
                  control={control}
                  render={({ field, fieldState }) => (
                    <DecimalInput
                      value={field.value}
                      onChange={field.onChange}
                      label="Impurezas %"
                      placeholder=" "
                      isInvalid={!!fieldState.error}
                    />
                  )}
                />
              </Col>
              <Col xs={6} md={2}>
                <Controller
                  name="impurezaWeight"
                  control={control}
                  render={({ field, fieldState }) => (
                    <WeightInput
                      value={field.value}
                      onChange={field.onChange}
                      label="Peso Impurezas"
                      isInvalid={!!fieldState.error}
                      placeholder=""
                      readOnly={true}
                    />
                  )}
                />
              </Col>
              <Col xs={6} md={2}>
                <Controller
                  name="ardido"
                  control={control}
                  render={({ field, fieldState }) => (
                    <DecimalInput
                      value={field.value}
                      onChange={field.onChange}
                      label="Ardidos %"
                      placeholder=" "
                      isInvalid={!!fieldState.error}
                    />
                  )}
                />
              </Col>
              <Col xs={6} md={2}>
                <Controller
                  name="ardidoWeight"
                  control={control}
                  render={({ field, fieldState }) => (
                    <WeightInput
                      value={field.value}
                      onChange={field.onChange}
                      label="Peso Ardidos"
                      placeholder=""
                      isInvalid={!!fieldState.error}
                      readOnly={true}
                    />
                  )}
                />
              </Col>
            </Row>
            <Row>
              <Col xs={6} md={2}>
                <Controller
                  name="verde"
                  control={control}
                  render={({ field, fieldState }) => (
                    <DecimalInput
                      value={field.value}
                      onChange={field.onChange}
                      label="Verdes %"
                      placeholder=" "
                      isInvalid={!!fieldState.error}
                    />
                  )}
                />
              </Col>
              <Col xs={6} md={2}>
                <Controller
                  name="verdeWeight"
                  control={control}
                  render={({ field, fieldState }) => (
                    <WeightInput
                      value={field.value}
                      onChange={field.onChange}
                      label="Peso Verdes"
                      isInvalid={!!fieldState.error}
                      placeholder=""
                      readOnly={true}
                    />
                  )}
                />
              </Col>
              <Col xs={6} md={2}>
                <Controller
                  name="quebrado"
                  control={control}
                  render={({ field, fieldState }) => (
                    <DecimalInput
                      value={field.value}
                      onChange={field.onChange}
                      label="Quebrados %"
                      placeholder=" "
                      isInvalid={!!fieldState.error}
                    />
                  )}
                />
              </Col>
              <Col xs={6} md={2}>
                <Controller
                  name="quebradoWeight"
                  control={control}
                  render={({ field, fieldState }) => (
                    <WeightInput
                      value={field.value}
                      onChange={field.onChange}
                      label="Peso Quebrados"
                      isInvalid={!!fieldState.error}
                      placeholder=""
                      readOnly={true}
                    />
                  )}
                />
              </Col>
              <Col xs={6} md={2}>
                <Controller
                  name="avariado"
                  control={control}
                  render={({ field, fieldState }) => (
                    <DecimalInput
                      value={field.value}
                      onChange={field.onChange}
                      label="Avariados %"
                      placeholder=" "
                      isInvalid={!!fieldState.error}
                      readOnly={true}
                    />
                  )}
                />
              </Col>
              <Col xs={6} md={2}>
                <Controller
                  name="avariadoWeight"
                  control={control}
                  render={({ field, fieldState }) => (
                    <WeightInput
                      value={field.value}
                      onChange={field.onChange}
                      label="Peso Avariados"
                      isInvalid={!!fieldState.error}
                      placeholder=""
                      readOnly={true}
                    />
                  )}
                />
              </Col>
            </Row>
            <Row>
              <Col md={2}>
                <Controller
                  name="ratePerc"
                  control={control}
                  render={({ field, fieldState }) => (
                    <DecimalInput
                      value={field.value}
                      onChange={field.onChange}
                      label="Tx. Serviço %"
                      isInvalid={!!fieldState.error}
                    />
                  )}
                />
              </Col>
              <Col md={2}>
                <Controller
                  name="rateKg"
                  control={control}
                  render={({ field, fieldState }) => (
                    <WeightInput
                      value={field.value}
                      onChange={field.onChange}
                      label="Tx. Serviço Kg"
                      isInvalid={!!fieldState.error}
                      readOnly={!hasAdminRole}
                    />
                  )}
                />
              </Col>
              <Col md={2}>
                <Controller
                  name="labelDiscount"
                  control={control}
                  render={({ field, fieldState }) => (
                    <WeightInput
                      value={field.value}
                      onChange={field.onChange}
                      label="Total Descontos"
                      isInvalid={!!fieldState.error}
                      readOnly={true}
                    />
                  )}
                />
              </Col>
              <Col md={6}>
                <TextAreaInput
                  label="Informações Adicionais"
                  name="observations"
                  placeholder="Digite suas observações"
                  rows={1}
                  register={register}
                  rules={{
                    required: false,
                    maxLength: {
                      value: 10000,
                      message: 'Máximo de 10.000 caracteres permitidos',
                    },
                  }}
                  errors={errors}
                />
              </Col>
            </Row>
            <Button variant="primary" className="button-custom w-100 mt-3" type="submit" disabled={isSubmitting}>
              Gravar
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      <ErrorModal
        show={showErrorModal}
        handleClose={handleCloseErrorModal}
        errorMessage={errorMessage}
      />

      <SuccessModal
        show={showSuccessModal}
        handleClose={handleCloseSuccessModal}
        successMessage={successMessage}
      />
    </>
  );
};

export default CreateRomaneioModal;